import { useState } from 'react';
import { Loader2, Play, Plug2, CheckCircle2, XCircle, AlertTriangle, Key } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Verdict({ value }) {
  if (value === 'PASS') {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" />
        PASS
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]">
      <XCircle className="h-3 w-3" />
      FAIL
    </Badge>
  );
}

function BoolBadge({ value, trueLabel, falseLabel }) {
  if (value) {
    return <span className="text-green-600 dark:text-green-400">{trueLabel || 'yes'}</span>;
  }
  return <span className="text-red-500 dark:text-red-400">{falseLabel || 'no'}</span>;
}

function SigningResult({ data }) {
  if (!data) {
    return <span className="text-muted-foreground">n/a</span>;
  }
  if (data.pass) {
    return (
      <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
        <Key className="h-3 w-3" />
        key valid, JWT minted
      </span>
    );
  }
  return (
    <span className="text-red-500 dark:text-red-400 flex items-center gap-1">
      <AlertTriangle className="h-3 w-3" />
      {data.error || 'failed'}
    </span>
  );
}

function ProviderSection({ provider, rows }) {
  const providerLabel = {
    revolut: 'Revolut',
    bridge: 'Bridge (BNP)',
    openbanking: 'Open Banking (TrueLayer)',
  }[provider] || provider;

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {providerLabel} ({rows.length} {rows.length === 1 ? 'account' : 'accounts'})
      </h4>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-3 py-1.5 font-medium">Account</th>
              <th className="text-left px-3 py-1.5 font-medium">Entity</th>
              <th className="text-left px-3 py-1.5 font-medium">Env</th>
              <th className="text-left px-3 py-1.5 font-medium">Redirect URI</th>
              <th className="text-left px-3 py-1.5 font-medium">Redirect</th>
              <th className="text-left px-3 py-1.5 font-medium">Callback</th>
              <th className="text-left px-3 py-1.5 font-medium">UI Button</th>
              {provider === 'revolut' && (
                <th className="text-left px-3 py-1.5 font-medium">Signing</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const c = row.checks;
              return (
                <tr key={row.account_id} className="border-b last:border-0">
                  <td className="px-3 py-1.5 font-mono font-medium">
                    {row.label}
                    <span className="text-muted-foreground ml-1">({row.account_id})</span>
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">{row.entity_name}</td>
                  <td className="px-3 py-1.5">
                    <div className="space-y-0.5">
                      <div>client_id: <BoolBadge value={c.env_present.client_id} /></div>
                      {c.env_present.private_key !== undefined && (
                        <div>private_key: <BoolBadge value={c.env_present.private_key} /></div>
                      )}
                      {c.env_present.client_secret !== undefined && (
                        <div>client_secret: <BoolBadge value={c.env_present.client_secret} /></div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10px]">
                    {c.redirect_uri.effective_uris ? (
                      c.redirect_uri.effective_uris.map((uri, i) => (
                        <div key={i} className="break-all">{uri}</div>
                      ))
                    ) : (
                      <div className="break-all">{c.redirect_uri.value}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <Verdict value={c.redirect_uri.verdict} />
                    {c.redirect_uri.note && (
                      <span className="text-[10px] text-muted-foreground block mt-0.5">{c.redirect_uri.note}</span>
                    )}
                    {c.redirect_uri.verdict === 'FAIL' && !c.redirect_uri.effective_uris && !c.redirect_uri.is_production && (
                      <span className="text-[10px] text-red-500 block mt-0.5">non-prod URL</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="space-y-0.5">
                      <Verdict value={c.callback_route.verdict} />
                      <div className="text-[10px] text-muted-foreground">
                        mounted: <BoolBadge value={c.callback_route.mounted} />
                        {' '}public: <BoolBadge value={c.callback_route.in_public_routes} />
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <BoolBadge value={c.ui_button.present} trueLabel="present" falseLabel="absent" />
                  </td>
                  {provider === 'revolut' && (
                    <td className="px-3 py-1.5">
                      <SigningResult data={c.signing_dryrun} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ConnectReadinessPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.connectReadiness();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Group rows by provider
  const grouped = {};
  if (result) {
    for (const row of result.rows) {
      if (!grouped[row.provider]) grouped[row.provider] = [];
      grouped[row.provider].push(row);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Plug2 className="h-5 w-5 text-blue-600" />
            Connect Readiness
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pre-flight check before clicking Connect. Per-account env, redirect URI, callback route mount, UI button, and Revolut signing dry-run. Read-only, no side effects.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-5">
          {/* Summary banner */}
          <div className={`border rounded-lg p-3 ${result.all_pass ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {result.all_pass ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">All checks pass ({result.total_accounts} accounts)</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">{result.failures.length} failure(s) across {result.total_accounts} accounts</span>
                </>
              )}
            </div>
            {result.failures.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs text-red-600 dark:text-red-400">
                {result.failures.map((f, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Provider sections */}
          {Object.entries(grouped).map(([provider, rows]) => (
            <ProviderSection key={provider} provider={provider} rows={rows} />
          ))}

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
